// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// sim.ts — the whole battle, as a pure function of (seed, mode, command log).
//
// NOBODY IS AUTHORITATIVE. Nothing about a position, a hit point or a death is ever transmitted:
// both devices run this file at 20 Hz from the same log and are supposed to be watching the same
// film. Host transfer is therefore a repaint, a late command is a re-derive, and a lost datagram is
// nothing at all — but the price is that every quantity here must be an INTEGER and every ordering
// must be TOTAL. The rules that buys:
//
//   • No Math.random, no Date.now, no sin/cos/atan2/pow/hypot, no decimal literals. IEEE-754 pins
//     +, −, × and ÷ to the bit and says nothing whatsoever about the transcendentals, which really
//     do differ in the last place between JavaScript engines. Positions are in 1/256ths of a tile,
//     ranges are compared squared, income is milli-ore per tick with an explicit remainder, and
//     angles never exist. tests/source-hygiene.test.ts fails the build on any of them.
//   • Every loop over entities runs in ascending id order. Never over a Set or a Map: insertion
//     order is observable and the two peers do not insert in the same order.
//   • Every comparison ends in a unit-id tie-break, so no two candidates can ever compare equal.
//
// The simulation makes ZERO random draws. The map is hand-authored, damage has no variance, and
// every tie is broken by id — so the shared seed is used only to decide which side is which and for
// render-side sparks, which live in a module this one cannot import.

import { modeOf, rustCenti, type Mode } from './modes';
import {
  COLS,
  ROWS,
  HALF,
  MIRROR_X,
  MIRROR_Y,
  SUB,
  TILES,
  Terrain,
  buildMap,
  chebyshev,
  colOf,
  idx,
  lineClear,
  rowOf,
  type MapData,
} from './map';
import { FORGE_ARMOUR, FORGE_HP, SPALL_EVERY, UNITS, UnitType, applied, tonnageOf } from './units';
import { findPath, type PathQuery } from './path';

/** Bump when the wire meaning of a command or the rules change — a mismatch is a stale tab. */
export const RULES_REV = 1;

export const enum Cmd {
  Move = 0,
  Attack = 1,
  Stance = 2,
  Build = 3,
  Rally = 4,
}

export const enum Stance {
  Hold = 0,
  Push = 1,
  Run = 2,
}

/** Ticks of input delay. A command issued now executes six ticks (300 ms) from now on every peer. */
export const CMD_DELAY = 6;

/** A command is exactly four integers: tick, seat*16+kind, entity (or −1), argument. */
export const CMD_WORDS = 4;

const RETARGET = 8;
const REPATH = 40;
const SEPARATE2 = 159 * 159;
const CAPTURE_TICKS = 40;
const SALVAGE_MILLI = 400;
const RUST_MILLI = 50;
const RUN_BONUS_NUM = 5;
const RUN_BONUS_DEN = 4;
/** Three seconds with nothing alive, nothing building and no money is not a comeback. */
const BROKEN_TICKS = 60;
/** theline's Foundry is one logical seam anchored here; its footprint is map.foundryTiles. */
export const FOUNDRY_TILE = idx(5, 7);
/** Hoisted: checkEnd runs every tick and Math.min over a mapped array allocates. */
const CHEAPEST = UNITS.reduce((m, u) => (u.cost < m ? u.cost : m), 1 << 30);

export interface Wreck {
  tile: number;
  ore: number;
  milli: number;
}

export interface Seam {
  tile: number;
  owner: number;
  ore: number;
  milli: number;
  /** Milli-ore per tick while held uncontested. Stored, never re-derived from the mode per tick. */
  rate: number;
  cap: number;
  capSide: number;
  /** theline's single objective. Presence is "standing on the block", not the 3x3. */
  foundry: boolean;
  /** True while both sides stand on it: income frozen, capture rewinding. */
  contested: boolean;
}

export interface SimEvent {
  kind: 'shot' | 'kill' | 'spall' | 'build' | 'claim' | 'lose' | 'salvage' | 'forge' | 'over';
  tick: number;
  seat: number;
  /** Shooter / dying unit / built unit. */
  unit: number;
  /** Target unit, target tile, unit type or ore amount, depending on kind. */
  at: number;
  amount: number;
}

export interface Side {
  reserve: number;
  milli: number;
  forgeHp: number;
  queue: number[];
  progress: number;
  presses: number;
  rally: number;
  banked: number;
  brokenFor: number;
  /** Ledger, for the results screen and for the conservation audit. */
  spent: number;
  mined: number;
  salvaged: number;
  built: number;
  lost: number;
  kills: number;
}

export interface State {
  seed: number;
  modeId: string;
  mode: Mode;
  map: MapData;
  tick: number;
  /** How far into the command log we have already consumed. Never rewound; a rollback re-derives. */
  cursor: number;
  over: boolean;
  /** −1 while the match is live, 0/1 for a winner, 2 for a draw. */
  winner: number;
  /** How the match ended, for the results screen. */
  ending: 'forge' | 'broken' | 'bank' | 'tonnage' | '';
  nextId: number;
  seat: Uint8Array;
  /**
   * This unit's index WITHIN ITS OWN SEAT, in creation order.
   *
   * Entity ids are global and interleaved, so anything keyed on an id is keyed on which seat
   * happened to build first. The bot staggers its per-unit thinking so twelve units do not all
   * re-path on one tick, and staggering by entity id meant seat 0 planned on tick 0 while seat 1
   * planned on tick 2 — a permanent two-tick head start that read as an eight-nil seat bias in the
   * very first headless run. The ordinal is the same number on both sides of a mirrored board.
   */
  ord: Uint8Array;
  type: Uint8Array;
  x: Int32Array;
  y: Int32Array;
  hp: Int32Array;
  armour: Int8Array;
  hits: Int32Array;
  alive: Uint8Array;
  cool: Int32Array;
  setup: Int32Array;
  stance: Uint8Array;
  dest: Int32Array;
  foe: Int32Array;
  pathAge: Int32Array;
  paths: number[][];
  pi: Int32Array;
  chip: Int32Array;
  sides: Side[];
  seams: Seam[];
  wrecks: Wreck[];
  /** Ore created by deaths and destroyed by rust — the two places the world's total moves. */
  created: number;
  rusted: number;
  /**
   * Per-seat income, as a percentage. It is 100/100 in every multiplayer match and in every fairness
   * measurement, and the balance sim asserts that. The ONE thing solo's easy tier changes is this,
   * set to 80 for the bot — a declared handicap that makes the bot poorer, never the player richer,
   * and never an information advantage (there is no fog of war here, so the bot cannot see anything
   * the player cannot). Every other difference between the tiers is competence.
   */
  handicap: Int32Array;
}

const CAP = 96;

const newSide = (mode: Mode, seat: number): Side => ({
  reserve: mode.startReserve,
  milli: 0,
  forgeHp: FORGE_HP,
  queue: [],
  progress: 0,
  presses: 0,
  // Just outside your own gate, and 180°-symmetric. (5,11) is a gate ridge, which would make every
  // fresh unit path to a tile it can never stand on and stop dead one square short of it.
  rally: seat === 0 ? idx(4, 11) : idx(7, 4),
  banked: 0,
  brokenFor: 0,
  spent: 0,
  mined: 0,
  salvaged: 0,
  built: 0,
  lost: 0,
  kills: 0,
});

export const tileOf = (s: State, e: number): number =>
  idx(Math.min(COLS - 1, Math.max(0, (s.x[e] / SUB) | 0)), Math.min(ROWS - 1, Math.max(0, (s.y[e] / SUB) | 0)));

const centre = (t: number): [number, number] => [colOf(t) * SUB + HALF, rowOf(t) * SUB + HALF];

export function spawn(s: State, seat: number, type: number, tile: number): number {
  const e = s.nextId++;
  const [cx, cy] = centre(tile);
  s.seat[e] = seat;
  let ord = 0;
  for (let o = 0; o < e; o++) if (s.seat[o] === seat) ord++;
  s.ord[e] = ord;
  s.type[e] = type;
  s.x[e] = cx;
  s.y[e] = cy;
  s.hp[e] = UNITS[type].hp;
  s.armour[e] = UNITS[type].armour;
  s.hits[e] = 0;
  s.alive[e] = 1;
  s.cool[e] = 0;
  s.setup[e] = 0;
  s.stance[e] = Stance.Push;
  s.dest[e] = -1;
  s.foe[e] = -1;
  s.pathAge[e] = 0;
  s.paths[e] = [];
  s.pi[e] = 0;
  s.chip[e] = 0;
  return e;
}

export function createState(seed: number, modeId: string, handicap: readonly number[] = [100, 100]): State {
  const mode = modeOf(modeId);
  const map = buildMap();
  const s: State = {
    seed: seed >>> 0,
    modeId: mode.id,
    mode,
    map,
    tick: 0,
    cursor: 0,
    over: false,
    winner: -1,
    ending: '',
    nextId: 0,
    seat: new Uint8Array(CAP),
    ord: new Uint8Array(CAP),
    type: new Uint8Array(CAP),
    x: new Int32Array(CAP),
    y: new Int32Array(CAP),
    hp: new Int32Array(CAP),
    armour: new Int8Array(CAP),
    hits: new Int32Array(CAP),
    alive: new Uint8Array(CAP),
    cool: new Int32Array(CAP),
    setup: new Int32Array(CAP),
    stance: new Uint8Array(CAP),
    dest: new Int32Array(CAP),
    foe: new Int32Array(CAP),
    pathAge: new Int32Array(CAP),
    paths: [],
    pi: new Int32Array(CAP),
    chip: new Int32Array(CAP),
    sides: [newSide(mode, 0), newSide(mode, 1)],
    seams: [],
    wrecks: [],
    created: 0,
    rusted: 0,
    handicap: Int32Array.from([handicap[0] ?? 100, handicap[1] ?? 100]),
  };

  for (const site of map.seams) {
    // In theline the four neutral seams are replaced by one Foundry, so they are simply not created.
    if (mode.foundry && site.owner === 2) continue;
    const home = site.owner !== 2;
    s.seams.push({
      tile: site.tile,
      owner: site.owner,
      ore: home ? mode.homeOre : mode.centralOre,
      milli: 0,
      rate: home ? mode.homeRate : mode.centralRate,
      cap: home ? CAPTURE_TICKS : 0,
      capSide: site.owner,
      foundry: false,
      contested: false,
    });
  }
  if (mode.foundry) {
    s.seams.push({
      tile: FOUNDRY_TILE,
      owner: 2,
      ore: mode.foundryOre,
      milli: 0,
      rate: mode.foundryRate,
      cap: 0,
      capSide: 2,
      foundry: true,
      contested: false,
    });
  }
  s.seams.sort((a, b) => a.tile - b.tile);

  // Openings. Both seats are placed in the same loop from 180°-symmetric tiles, so the starting
  // position is provably equal — tests/rules.test.ts asserts the mirror, and the balance sim
  // asserts the bot finds no seat advantage in the result.
  for (let seat = 0; seat < 2; seat++) {
    if (mode.deployed) {
      for (const [type, tile] of map.deployment[seat]) spawn(s, seat, type, tile);
    } else {
      for (const tile of map.openings[seat]) spawn(s, seat, UnitType.Harrier, tile);
    }
  }
  return s;
}

/** Who is standing on a seam — the 3x3 around it, or anywhere on the Foundry block. */
function presence(s: State, seam: Seam): [boolean, boolean] {
  let a = false;
  let b = false;
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    const t = tileOf(s, e);
    const near = seam.foundry ? s.map.foundryTiles.includes(t) : chebyshev(t, seam.tile) <= 1;
    if (!near) continue;
    if (s.seat[e] === 0) a = true;
    else b = true;
    if (a && b) break;
  }
  return [a, b];
}

// ── line of fire ────────────────────────────────────────────────────────────────
/**
 * The opacity grid for this tick: static terrain, plus every wreck, plus every living ANVIL.
 *
 * Only the Anvil blocks, and that is measured rather than aesthetic — see the note on `blocks` in
 * units.ts. Rebuilt once per tick in ascending entity order, never incrementally, so it cannot drift.
 */
const opaqueBuf = new Uint8Array(TILES);
export function opacity(s: State): Uint8Array {
  opaqueBuf.set(s.map.opaque);
  for (const w of s.wrecks) opaqueBuf[w.tile] = 1;
  for (let e = 0; e < s.nextId; e++) {
    if (s.alive[e] && UNITS[s.type[e]].blocks) opaqueBuf[tileOf(s, e)] = 1;
  }
  return opaqueBuf;
}

/** Is the line between two tiles clear? Neither endpoint's own tile ever blocks. */
export function canSee(op: Uint8Array, from: number, to: number): boolean {
  return lineClear(from, to, (t) => t !== from && t !== to && op[t] === 1);
}

const dist2 = (s: State, a: number, b: number): number => {
  const dx = s.x[a] - s.x[b];
  const dy = s.y[a] - s.y[b];
  return dx * dx + dy * dy;
};

/**
 * Target choice, and the one place a "shot past a closer enemy" bug would live.
 *
 * Score is deliberately simple and total: nearer is better, hurt is better, an Arbalest is worth
 * more than anything else its size, and stripped armour is worth finishing. Every comparison ends in
 * a unit-id tie-break so two candidates can never compare equal on two different devices.
 * tests/mechanism.test.ts re-derives this from outside the simulation and audits every shot at zero
 * tolerance.
 */
export function scoreTarget(s: State, e: number, t: number): number {
  const d = chebyshev(tileOf(s, e), tileOf(s, t));
  let v = 1000 - d * 20;
  v -= (s.hp[t] * 10) / UNITS[s.type[t]].hp;
  if (s.type[t] === UnitType.Arbalest) v += 120;
  if (s.armour[t] === 0) v += 60;
  return v | 0;
}

function pickTarget(s: State, op: Uint8Array, e: number): number {
  const spec = UNITS[s.type[e]];
  const from = tileOf(s, e);
  let best = -1;
  let bestScore = -(1 << 30);
  let bestKey = 1 << 30;
  for (let t = 0; t < s.nextId; t++) {
    if (!s.alive[t] || s.seat[t] === s.seat[e]) continue;
    if (dist2(s, e, t) > spec.range2) continue;
    if (!canSee(op, from, tileOf(s, t))) continue;
    const sc = scoreTarget(s, e, t);
    // The tie-break is the target's TILE, mirrored for seat 1, then its id — never the raw id on
    // its own. Ids are handed out in creation order across both seats, so "lowest id wins" means
    // "prefer the north-eastern one" for everybody, which is a seat advantage wearing a tie-break's
    // clothes.
    const k = seatKey(s.seat[e], tileOf(s, t)) * 256 + t;
    if (sc > bestScore || (sc === bestScore && k < bestKey)) {
      bestScore = sc;
      bestKey = k;
      best = t;
    }
  }
  return best;
}

// ── damage ──────────────────────────────────────────────────────────────────────
/**
 * DAMAGE IS SIMULTANEOUS, AND THAT IS A FAIRNESS RULE RATHER THAN A FLAVOUR ONE.
 *
 * The natural way to write this is to apply each hit as the shooter takes it, inside the unit loop.
 * That loop runs in ascending entity id, and ids are handed out in creation order — so seat 0's
 * opening pair always shoots before seat 1's, and in every mutual kill on the board seat 0 wins by
 * one iteration. It is invisible, it is systematic, and in the first headless run of this game it
 * was worth the entire match.
 *
 * So a tick has two passes. Pass A only RECORDS: how much each victim was hit for, computed against
 * the armour it had at the START of the tick, and how many times. Pass B applies the totals and
 * resolves the deaths. Nothing a shooter does can depend on what another shooter did in the same
 * twentieth of a second, spalling is a function of the hit COUNT rather than of the order the hits
 * arrived in, and two units that kill each other on the same tick both die.
 */
const pendDmg = new Int32Array(CAP);
const pendHits = new Int32Array(CAP);
const pendBy = new Int32Array(CAP);
const armourAt = new Int8Array(CAP);
/**
 * MOVEMENT IS SIMULTANEOUS FOR THE SAME REASON DAMAGE IS.
 *
 * Writing `s.x[e] += step` inside the unit loop means unit 3 plans against a world in which units 0
 * to 2 have ALREADY moved this tick, while unit 0 planned against the world as it was. On a
 * point-symmetric map with identical armies that is a systematic advantage for whichever seat holds
 * the lower entity ids, and it survived every other symmetry fix in this file: the two openings
 * stayed pixel-perfect mirrors for ninety-two ticks and then one side got the first shot off.
 *
 * So the loop only proposes. Everything reads the world as it was at the top of the tick.
 */
const pendX = new Int32Array(CAP);
const pendY = new Int32Array(CAP);
const pendStep = new Uint8Array(CAP);
const pushX = new Int32Array(CAP);
const pushY = new Int32Array(CAP);

function record(victim: number, raw: number, by: number): void {
  pendDmg[victim] += applied(raw, armourAt[victim]);
  pendHits[victim]++;
  // Credit the kill to the lowest-id shooter that contributed, purely so the tally is reproducible.
  if (pendBy[victim] < 0 || by < pendBy[victim]) pendBy[victim] = by;
}

function resolveDamage(s: State, ev: SimEvent[]): void {
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e] || pendHits[e] === 0) continue;
    const before = s.hits[e];
    s.hits[e] += pendHits[e];
    s.hp[e] -= pendDmg[e];
    const steps = ((s.hits[e] / SPALL_EVERY) | 0) - ((before / SPALL_EVERY) | 0);
    if (steps > 0 && s.armour[e] > 0) {
      s.armour[e] = Math.max(0, s.armour[e] - steps);
      ev.push({ kind: 'spall', tick: s.tick, seat: s.seat[e], unit: e, at: s.armour[e], amount: 0 });
    }
    if (s.hp[e] <= 0) kill(s, e, pendBy[e], ev);
  }
}

function kill(s: State, victim: number, by: number, ev: SimEvent[]): void {
  if (!s.alive[victim]) return;
  s.alive[victim] = 0;
  s.hp[victim] = 0;
  const seat = s.seat[victim];
  const ore = UNITS[s.type[victim]].wreck;
  const tile = tileOf(s, victim);
  s.sides[seat].lost++;
  if (by >= 0) s.sides[s.seat[by]].kills++;
  // A body is money AND cover. A second death on the same tile tops the wreck up rather than
  // stacking, capped, so a corpse pile cannot become an unlimited bank.
  //
  // `created` counts what the cap ACTUALLY allowed in, not what the body was worth. Counting the
  // full value here was a real ore leak: the ledger diverged the first time two units died on one
  // tile and stayed diverged for the rest of the match, which is exactly the kind of quiet
  // arithmetic error the zero-tolerance conservation audit exists to catch.
  const existing = s.wrecks.find((w) => w.tile === tile);
  const added = existing ? Math.min(60 - existing.ore, ore) : ore;
  if (existing) existing.ore += added;
  else s.wrecks.push({ tile, ore, milli: 0 });
  s.wrecks.sort((a, b) => a.tile - b.tile);
  s.created += added > 0 ? added : 0;
  ev.push({ kind: 'kill', tick: s.tick, seat, unit: victim, at: tile, amount: ore });
}

// ── production ──────────────────────────────────────────────────────────────────
export function aliveCount(s: State, seat: number): number {
  let n = 0;
  for (let e = 0; e < s.nextId; e++) if (s.alive[e] && s.seat[e] === seat) n++;
  return n;
}

export function canBuild(s: State, seat: number, type: number): boolean {
  const side = s.sides[seat];
  const mode = s.mode;
  if (s.over) return false;
  if (mode.forgeKill && side.forgeHp <= 0) return false;
  if (mode.buildBudget > 0 && side.presses >= mode.buildBudget) return false;
  if (side.queue.length >= 2) return false;
  if (aliveCount(s, seat) + side.queue.length >= mode.unitCap) return false;
  return side.reserve >= UNITS[type].cost;
}

function press(s: State, seat: number, type: number): void {
  if (!canBuild(s, seat, type)) return;
  const side = s.sides[seat];
  // Ore leaves the moment you press, and there is no cancel and no refund. That is the whole point:
  // the player who read the attack correctly pressed ANVIL before the attack existed.
  side.reserve -= UNITS[type].cost;
  side.spent += UNITS[type].cost;
  side.presses++;
  side.queue.push(type);
}

/**
 * A TIE-BREAK THAT IS THE SAME FOR BOTH SEATS.
 *
 * Every "nearest tile, lowest index wins" rule in this game is seat-relative, and a raw tile index
 * is not: the board runs north to south, so "lowest index" means "toward seat 1's end" for
 * everybody. That is not a rounding difference. Seat 0's reinforcements spawned one tile FORWARD of
 * its Forge and seat 1's spawned one tile BACK from its own, and the first headless run of this
 * game read 0–8 across all three modes from that alone. Mirroring the key for seat 1 makes the
 * tie-break rotate with the map, which is what point symmetry requires.
 */
export const seatKey = (seat: number, t: number): number => (seat === 0 ? t : TILES - 1 - t);

/** The free tile nearest the Forge, searched in a seat-symmetric order so both peers agree. */
function spawnTile(s: State, seat: number): number {
  const home = s.map.forgeTiles[seat];
  const occupied = new Uint8Array(TILES);
  for (let e = 0; e < s.nextId; e++) if (s.alive[e]) occupied[tileOf(s, e)] = 1;
  const anchor = seat === 0 ? idx(5, 13) : idx(6, 2);
  let best = -1;
  let bestD = 1 << 30;
  let bestKey = 1 << 30;
  for (let t = 0; t < TILES; t++) {
    if (s.map.terrain[t] === Terrain.Ridge || s.map.terrain[t] === Terrain.Forge) continue;
    if (occupied[t]) continue;
    const d = chebyshev(t, anchor);
    const k = seatKey(seat, t);
    if (d < bestD || (d === bestD && k < bestKey)) {
      bestD = d;
      bestKey = k;
      best = t;
    }
  }
  return best >= 0 ? best : home[0];
}

// ── the tick ────────────────────────────────────────────────────────────────────
/**
 * Consume the commands scheduled for this tick.
 *
 * The log is sorted by (tick, seat, kind, unit, arg) and only ever grows at the FUTURE end, so a
 * cursor is safe and turns an O(log × ticks) rescan into O(log). That matters: a four-minute match
 * is 4800 ticks, and rescanning a two-hundred-command log every one of them is most of the balance
 * sim's runtime. A rollback re-derives from tick zero, which resets the cursor with the state.
 */
function applyCommands(s: State, log: readonly number[]): void {
  while (s.cursor + CMD_WORDS <= log.length && log[s.cursor] < s.tick) s.cursor += CMD_WORDS;
  for (let i = s.cursor; i + CMD_WORDS <= log.length; i += CMD_WORDS) {
    if (log[i] > s.tick) break;
    const seat = (log[i + 1] >> 4) & 15;
    const kind = log[i + 1] & 15;
    const e = log[i + 2];
    const arg = log[i + 3];
    if (seat > 1) continue;
    if (kind === Cmd.Build) {
      if (arg >= 0 && arg < UNITS.length) press(s, seat, arg);
      continue;
    }
    if (kind === Cmd.Rally) {
      if (arg >= 0 && arg < TILES) s.sides[seat].rally = arg;
      continue;
    }
    if (e < 0 || e >= s.nextId || !s.alive[e] || s.seat[e] !== seat) continue;
    if (kind === Cmd.Stance) {
      if (arg >= 0 && arg <= 2) s.stance[e] = arg;
    } else if (kind === Cmd.Move) {
      if (arg < 0 || arg >= TILES) continue;
      s.dest[e] = arg;
      s.foe[e] = -1;
      s.pathAge[e] = REPATH;
      if (s.stance[e] === Stance.Hold) s.stance[e] = Stance.Push;
    } else if (kind === Cmd.Attack) {
      if (arg < 0 || arg >= s.nextId || s.seat[arg] === seat) continue;
      s.foe[e] = arg;
      s.dest[e] = -1;
      s.pathAge[e] = REPATH;
    }
  }
}

function economy(s: State, ev: SimEvent[]): void {
  for (const seam of s.seams) {
    const [a, b] = presence(s, seam);
    const solo = a !== b ? (a ? 0 : 1) : -1;
    seam.contested = a && b;
    if (solo >= 0 && solo !== seam.owner) {
      if (seam.capSide !== solo) {
        seam.capSide = solo;
        seam.cap = 0;
      }
      seam.cap++;
      if (seam.cap >= CAPTURE_TICKS) {
        const before = seam.owner;
        seam.owner = solo;
        seam.cap = CAPTURE_TICKS;
        ev.push({ kind: 'claim', tick: s.tick, seat: solo, unit: -1, at: seam.tile, amount: seam.ore });
        if (before !== 2) ev.push({ kind: 'lose', tick: s.tick, seat: before, unit: -1, at: seam.tile, amount: 0 });
      }
    } else if (seam.cap > 0 && seam.owner !== seam.capSide) {
      seam.cap--;
    }

    // The claim is STICKY — walk away and it keeps paying — but a single enemy body standing next to
    // it freezes the income instantly, two full seconds before it could ever flip. Denial is much
    // cheaper than capture, which is the trailing player's best lever: a thirty-ore Harrier parked
    // beside a home seam costs the leader one and a half ore a second for as long as it lives,
    // without killing anything.
    const enemyNear = seam.owner === 0 ? b : seam.owner === 1 ? a : false;
    if (seam.owner !== 2 && seam.ore > 0 && !enemyNear) {
      seam.milli += ((seam.rate * s.handicap[seam.owner]) / 100) | 0;
      const whole = (seam.milli / 1000) | 0;
      if (whole > 0) {
        seam.milli -= whole * 1000;
        const take = whole < seam.ore ? whole : seam.ore;
        seam.ore -= take;
        s.sides[seam.owner].reserve += take;
        s.sides[seam.owner].mined += take;
        if (seam.foundry) s.sides[seam.owner].banked += take;
      }
    }
  }

  for (let i = s.wrecks.length - 1; i >= 0; i--) {
    const w = s.wrecks[i];
    let a = false;
    let b = false;
    for (let e = 0; e < s.nextId; e++) {
      if (!s.alive[e]) continue;
      if (chebyshev(tileOf(s, e), w.tile) > 1) continue;
      if (s.seat[e] === 0) a = true;
      else b = true;
    }
    // You physically stand on the money, and only if nobody is arguing about it.
    const claimer = a !== b ? (a ? 0 : 1) : -1;
    w.milli += RUST_MILLI + (claimer >= 0 ? SALVAGE_MILLI : 0);
    const whole = (w.milli / 1000) | 0;
    if (whole > 0) {
      w.milli -= whole * 1000;
      const take = Math.min(whole, w.ore);
      w.ore -= take;
      if (claimer >= 0) {
        // Rust takes its cut first; the rest is banked.
        const rust = Math.min(take, (take * RUST_MILLI) / (RUST_MILLI + SALVAGE_MILLI)) | 0;
        const gained = take - rust;
        s.rusted += rust;
        s.sides[claimer].reserve += gained;
        s.sides[claimer].salvaged += gained;
        if (gained > 0) {
          ev.push({ kind: 'salvage', tick: s.tick, seat: claimer, unit: -1, at: w.tile, amount: gained });
        }
      } else {
        s.rusted += take;
      }
    }
    // Collecting a wreck DESTROYS THE COVER IT WAS. That is the trade the whole design turns on.
    if (w.ore <= 0) s.wrecks.splice(i, 1);
  }
}

function production(s: State, ev: SimEvent[]): void {
  for (let seat = 0; seat < 2; seat++) {
    const side = s.sides[seat];
    if (side.queue.length === 0) continue;
    if (s.mode.forgeKill && side.forgeHp <= 0) continue;
    side.progress++;
    const type = side.queue[0];
    if (side.progress >= UNITS[type].build) {
      side.progress = 0;
      side.queue.shift();
      const tile = spawnTile(s, seat);
      const e = spawn(s, seat, type, tile);
      s.dest[e] = side.rally;
      s.pathAge[e] = REPATH;
      side.built++;
      ev.push({ kind: 'build', tick: s.tick, seat, unit: e, at: tile, amount: type });
    }
  }
}

function stepUnit(s: State, op: Uint8Array, e: number, ev: SimEvent[]): void {
  const spec = UNITS[s.type[e]];
  if (s.cool[e] > 0) s.cool[e]--;
  if (s.setup[e] > 0) s.setup[e]--;

  // Retarget on a fixed cadence, and immediately if the current target is gone or out of sight.
  const from = tileOf(s, e);
  let tgt = s.foe[e];
  const stale =
    tgt < 0 ||
    !s.alive[tgt] ||
    dist2(s, e, tgt) > spec.range2 ||
    !canSee(op, from, tileOf(s, tgt));
  if (stale || (s.tick + e) % RETARGET === 0) {
    const found = pickTarget(s, op, e);
    // An explicit attack order survives losing sight of its victim; an automatic one does not.
    if (found >= 0) tgt = found;
    else if (stale) tgt = s.foe[e] >= 0 && s.alive[s.foe[e]] && s.dest[e] < 0 ? s.foe[e] : -1;
  }

  const inRange =
    tgt >= 0 && s.alive[tgt] && dist2(s, e, tgt) <= spec.range2 && canSee(op, from, tileOf(s, tgt));

  if (inRange && s.stance[e] !== Stance.Run) {
    if (s.cool[e] === 0 && s.setup[e] === 0) {
      s.cool[e] = spec.interval;
      ev.push({ kind: 'shot', tick: s.tick, seat: s.seat[e], unit: e, at: tgt, amount: spec.damage });
      record(tgt, spec.damage, e);
    }
    // PUSH stops to shoot; only an explicit move order pulls it off a target.
    if (s.dest[e] < 0) return;
  }

  // A Forge in reach is a target too, and it is the only one that has to be walked into.
  if (s.mode.forgeKill && s.stance[e] !== Stance.Run) {
    const enemy = 1 - s.seat[e];
    const fside = s.sides[enemy];
    if (fside.forgeHp > 0) {
      for (const ft of s.map.forgeTiles[enemy]) {
        const dx = s.x[e] - (colOf(ft) * SUB + HALF);
        const dy = s.y[e] - (rowOf(ft) * SUB + HALF);
        if (dx * dx + dy * dy > spec.range2) continue;
        if (!canSee(op, from, ft)) continue;
        if (s.cool[e] > 0 || s.setup[e] > 0) return;
        s.cool[e] = spec.interval;
        // The Forge does not spall: buildings require bodies, not a bigger gun.
        fside.forgeHp -= applied(spec.damage, FORGE_ARMOUR);
        ev.push({ kind: 'forge', tick: s.tick, seat: enemy, unit: e, at: ft, amount: spec.damage });
        if (fside.forgeHp <= 0) fside.forgeHp = 0;
        return;
      }
    }
  }

  if (s.stance[e] === Stance.Hold) return;

  // Where are we going? An attack order chases its victim; otherwise it is the move destination.
  let goal = -1;
  if (s.dest[e] >= 0) goal = s.dest[e];
  else if (s.foe[e] >= 0 && s.alive[s.foe[e]] && s.stance[e] === Stance.Push) goal = tileOf(s, s.foe[e]);
  if (goal < 0) return;

  s.pathAge[e]++;
  const path = s.paths[e];
  const needPath =
    s.pathAge[e] >= REPATH || path.length === 0 || s.pi[e] >= path.length || path[path.length - 1] !== goal;
  if (needPath) {
    s.pathAge[e] = 0;
    s.paths[e] = findPath(pathQuery(s, s.seat[e]), from, goal);
    s.pi[e] = 0;
  }
  const p = s.paths[e];
  if (s.pi[e] >= p.length) {
    if (s.dest[e] === from) s.dest[e] = -1;
    return;
  }

  const [tx, ty] = centre(p[s.pi[e]]);
  let sp = spec.speed;
  if (s.map.terrain[from] === Terrain.Scrub) sp = ((sp * 60) / 100) | 0;
  if (s.wrecks.some((w) => w.tile === from)) sp = ((sp * 50) / 100) | 0;
  if (s.stance[e] === Stance.Run) sp = ((sp * RUN_BONUS_NUM) / RUN_BONUS_DEN) | 0;
  if (sp < 1) sp = 1;
  const dx = tx - s.x[e];
  const dy = ty - s.y[e];
  const adx = dx < 0 ? -dx : dx;
  const ady = dy < 0 ? -dy : dy;
  // Eight-way movement, with the diagonal scaled by 181/256 so a diagonal step is not free. There
  // is no heading and therefore no trigonometry anywhere in the simulation.
  const stride = adx > 0 && ady > 0 ? ((sp * 181) / 256) | 0 : sp;
  const use = stride < 1 ? 1 : stride;
  pendX[e] = dx === 0 ? 0 : (adx < use ? adx : use) * (dx < 0 ? -1 : 1);
  pendY[e] = dy === 0 ? 0 : (ady < use ? ady : use) * (dy < 0 ? -1 : 1);
  pendStep[e] = 1;
  if (spec.setup > 0) s.setup[e] = spec.setup;
}

/** Commit the tick's proposed movement, then advance anyone who reached their next waypoint. */
function resolveMovement(s: State): void {
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e] || !pendStep[e]) continue;
    s.x[e] += pendX[e];
    s.y[e] += pendY[e];
    const p = s.paths[e];
    if (s.pi[e] >= p.length) continue;
    const t = p[s.pi[e]];
    if (s.x[e] === colOf(t) * SUB + HALF && s.y[e] === rowOf(t) * SUB + HALF) {
      s.pi[e]++;
      if (s.pi[e] >= p.length && s.dest[e] >= 0) s.dest[e] = -1;
    }
  }
}

export function pathQuery(s: State, seat: number): PathQuery {
  return {
    map: s.map,
    seat,
    extra: (t) => (s.wrecks.some((w) => w.tile === t) ? 100 : 0),
    blocked: () => false,
  };
}

/**
 * Keep bodies off each other's exact centres. Quantised to two sub-units along the dominant axis and
 * applied in ascending id order, so it is exactly reproducible — a float push would be the single
 * easiest way to desync this whole game.
 */
function separate(s: State): void {
  // TWO-PHASE, LIKE EVERYTHING ELSE IN A TICK. Pushing in place means pair (4,5) is resolved before
  // pair (4,9), while their mirror images (4,5) and (5,8) are resolved in the opposite relative
  // order — so two perfectly mirrored armies drift apart by two sub-units and, eight hundred ticks
  // later, one of them wins. Accumulating and committing makes the pass order irrelevant.
  for (let e = 0; e < s.nextId; e++) {
    pushX[e] = 0;
    pushY[e] = 0;
  }
  for (let a = 0; a < s.nextId; a++) {
    if (!s.alive[a]) continue;
    for (let b = a + 1; b < s.nextId; b++) {
      if (!s.alive[b]) continue;
      const dx = s.x[b] - s.x[a];
      const dy = s.y[b] - s.y[a];
      const d2 = dx * dx + dy * dy;
      if (d2 >= SEPARATE2) continue;
      if (d2 === 0) {
        // PERFECTLY STACKED, and there is no local tie-break that survives the mirror: both units
        // are at the same point, so anything derived from their positions is identical for the
        // mirrored pair, and anything derived from their ids is identical too. The one quantity
        // that does negate under a point reflection is which side of the board's centre they are
        // on — and the centre falls between two sub-units, because MIRROR_X is odd, so "which
        // side" is never ambiguous.
        const dir = s.x[a] * 2 > MIRROR_X ? 1 : -1;
        pushX[a] -= 2 * dir;
        pushX[b] += 2 * dir;
        continue;
      }
      const adx = dx < 0 ? -dx : dx;
      const ady = dy < 0 ? -dy : dy;
      if (adx >= ady) {
        const push = dx < 0 ? -2 : 2;
        pushX[a] -= push;
        pushX[b] += push;
      } else {
        const push = dy < 0 ? -2 : 2;
        pushY[a] -= push;
        pushY[b] += push;
      }
    }
  }
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    s.x[e] += pushX[e];
    s.y[e] += pushY[e];
  }
  // [0, MIRROR] is exactly the interval the reflection maps onto itself, so a unit shoved against
  // one wall and its opposite number shoved against the other stay each other's mirror image.
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    if (s.x[e] < 0) s.x[e] = 0;
    if (s.y[e] < 0) s.y[e] = 0;
    if (s.x[e] > MIRROR_X) s.x[e] = MIRROR_X;
    if (s.y[e] > MIRROR_Y) s.y[e] = MIRROR_Y;
  }
}

function rust(s: State, ev: SimEvent[]): void {
  const centi = rustCenti(s.mode, s.tick);
  if (centi === 0) return;
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    s.chip[e] += centi;
    const whole = (s.chip[e] / 100) | 0;
    if (whole <= 0) continue;
    s.chip[e] -= whole * 100;
    s.hp[e] -= whole;
    if (s.hp[e] <= 0) kill(s, e, -1, ev);
  }
  // RUST NEVER TOUCHES A FORGE, and that is a correctness rule rather than a balance one. Rust is
  // perfectly symmetric, so a Forge taking rust damage means both Forges reach zero on the SAME
  // tick, at which point "Forge destroyed → immediate win" fires for both seats and the winner is
  // undefined. Chipping the units alone is already decisive: a full Anvil dies to rust in under a
  // minute, so the armies cannot survive the clock even if the buildings can, and adjudication
  // resolves what is left.
}

function checkEnd(s: State, ev: SimEvent[]): void {
  if (s.over) return;
  const mode = s.mode;

  if (mode.forgeKill) {
    const down0 = s.sides[0].forgeHp <= 0;
    const down1 = s.sides[1].forgeHp <= 0;
    // Two Forges reaching zero on one tick is rare but it is reachable (a mutual push that lands in
    // the same twenty milliseconds), and "immediate win" firing twice has no answer. Say what
    // happens rather than leaving it to whichever seat the loop visits first.
    if (down0 && down1) {
      const a = tonnage(s, 0);
      const b = tonnage(s, 1);
      return finish(s, a === b ? 2 : a > b ? 0 : 1, 'tonnage', ev);
    }
    if (down0) return finish(s, 1, 'forge', ev);
    if (down1) return finish(s, 0, 'forge', ev);
  }
  if (mode.foundry) {
    for (let seat = 0; seat < 2; seat++) {
      if (s.sides[seat].banked >= mode.foundryTarget) return finish(s, seat, 'bank', ev);
    }
  }

  // "Broken": nothing alive, nothing building and not enough ore for the cheapest unit, for three
  // continuous seconds. Exact, never heuristic — a player who can still afford a Harrier is not out.
  const broken = [false, false];
  for (let seat = 0; seat < 2; seat++) {
    const side = s.sides[seat];
    const dead =
      aliveCount(s, seat) === 0 &&
      side.queue.length === 0 &&
      (side.reserve < CHEAPEST || (mode.buildBudget > 0 && side.presses >= mode.buildBudget) || (mode.forgeKill && side.forgeHp <= 0));
    side.brokenFor = dead ? side.brokenFor + 1 : 0;
    broken[seat] = side.brokenFor >= BROKEN_TICKS;
  }
  // Both sides can wipe each other out on the same tick, and with simultaneous damage that is a
  // NORMAL outcome rather than an edge case. Checking seat 0 first and returning would hand every
  // mutual annihilation to seat 1.
  if (broken[0] && broken[1]) {
    const a = tonnage(s, 0);
    const b = tonnage(s, 1);
    return finish(s, a === b ? 2 : a > b ? 0 : 1, 'tonnage', ev);
  }
  if (broken[0]) return finish(s, 1, 'broken', ev);
  if (broken[1]) return finish(s, 0, 'broken', ev);

  if (s.tick >= mode.hardEnd) {
    const a = tonnage(s, 0);
    const b = tonnage(s, 1);
    return finish(s, a === b ? 2 : a > b ? 0 : 1, 'tonnage', ev);
  }
}

function finish(s: State, winner: number, ending: State['ending'], ev: SimEvent[]): void {
  s.over = true;
  s.winner = winner;
  s.ending = ending;
  ev.push({ kind: 'over', tick: s.tick, seat: winner, unit: -1, at: 0, amount: 0 });
}

/**
 * The single number that answers "who is winning", displayed continuously as a split bar. It is
 * banked ore plus the depreciated value of everything still standing, which is why losing an Anvil
 * drops your bar by seventy in one step and salvaging its wreck raises theirs by thirty-five over
 * four seconds.
 */
export function tonnage(s: State, seat: number): number {
  const side = s.sides[seat];
  let v = side.reserve + (s.mode.foundry ? side.banked * 2 : 0);
  for (let e = 0; e < s.nextId; e++) if (s.alive[e] && s.seat[e] === seat) v += tonnageOf(s.type[e], s.hp[e]);
  if (s.mode.forgeKill) v += (side.forgeHp / 3) | 0;
  return v;
}

export function step(s: State, log: readonly number[], ev: SimEvent[]): void {
  if (s.over) return;
  applyCommands(s, log);
  production(s, ev);
  economy(s, ev);
  const op = opacity(s);
  for (let e = 0; e < s.nextId; e++) {
    pendDmg[e] = 0;
    pendHits[e] = 0;
    pendBy[e] = -1;
    pendStep[e] = 0;
    armourAt[e] = s.armour[e];
  }
  for (let e = 0; e < s.nextId; e++) if (s.alive[e]) stepUnit(s, op, e, ev);
  resolveMovement(s);
  resolveDamage(s, ev);
  separate(s);
  rust(s, ev);
  s.tick++;
  checkEnd(s, ev);
}

export function advanceTo(s: State, tick: number, log: readonly number[], ev: SimEvent[]): void {
  let guard = 0;
  while (s.tick < tick && !s.over && guard++ < 20000) step(s, log, ev);
}

/** A whole battle from nothing. This is what a rollback re-runs and what the balance sim calls. */
export function derive(
  seed: number,
  modeId: string,
  log: readonly number[],
  tick: number,
  handicap: readonly number[] = [100, 100],
): State {
  const s = createState(seed, modeId, handicap);
  advanceTo(s, tick, log, []);
  return s;
}

/**
 * FNV-1a over everything that matters. Exchanged every twenty ticks so a divergence is a visible
 * banner rather than two players quietly watching different films.
 */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v | 0;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  mix(s.tick);
  mix(s.nextId);
  for (let e = 0; e < s.nextId; e++) {
    mix(s.alive[e] ? 1 : 0);
    mix(s.x[e]);
    mix(s.y[e]);
    mix(s.hp[e]);
    mix(s.armour[e]);
    mix(s.stance[e]);
  }
  for (const side of s.sides) {
    mix(side.reserve);
    mix(side.forgeHp);
    mix(side.banked);
    mix(side.queue.length);
    mix(side.progress);
  }
  for (const seam of s.seams) {
    mix(seam.tile);
    mix(seam.owner);
    mix(seam.ore);
    mix(seam.cap);
  }
  for (const w of s.wrecks) {
    mix(w.tile);
    mix(w.ore);
  }
  return h >>> 0;
}

/**
 * The ore ledger. Ore enters the world only when a body dies (half its cost, as a wreck) and leaves
 * only through rust; everything else is a transfer. tests/mechanism.test.ts asserts this identity
 * every tick at zero tolerance, which is what would catch a seam paying twice or a wreck banking ore
 * it did not hold.
 */
export function ledger(s: State): { held: number; expected: number } {
  let held = s.rusted;
  for (const side of s.sides) held += side.reserve + side.spent;
  for (const seam of s.seams) held += seam.ore;
  for (const w of s.wrecks) held += w.ore;
  let expected = s.created + s.mode.startReserve * 2;
  for (const ore of startingSeams(s.mode.id)) expected += ore;
  return { held, expected };
}

/** The ore each seam held at t=0, in the same order createState builds them. */
export function startingSeams(modeId: string): number[] {
  const mode = modeOf(modeId);
  const map = buildMap();
  const out: number[] = [];
  if (mode.foundry) {
    for (const site of map.seams) if (site.owner !== 2) out.push(mode.homeOre);
    out.push(mode.foundryOre);
  } else {
    for (const site of map.seams) out.push(site.owner !== 2 ? mode.homeOre : mode.centralOre);
  }
  return out;
}
